import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import getConfig from "next/config";
import { isMobile } from "react-device-detect";
import { OverlayTrigger, Tooltip, Form } from "react-bootstrap";

import { hexToRGBA } from "../../../utils/theme";

const { publicRuntimeConfig } = getConfig();

const StyledGroup = styled(Form.Group)`
  position: relative;

  padding-left: 0;
  padding-right: 0;

  ${({ withLabel }) => withLabel && css`
    padding-top: 24px;
  `};
`;

const StyledLabel = styled(Form.Label)`
  position: absolute;
  left: 0;
  top: 0;
  font-family: 'LatoBold';
  font-size: 12px;
  line-height: 24px;
  text-transform: uppercase;
  white-space: nowrap;
  color: #078FF1;

  span {
    font-size: 16px;
    color: #C61F0C;
  }
`;

const StyledControl = styled(Form.Control)`
  resize: none;

  width: 100%;
  min-height: 52px;

  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 16px;
  padding-right: 10px;

  font-family: 'LatoRegular';
  font-size: 14px;
  line-height: 24px;
  color: ${hexToRGBA('#1B3858', 0.5)};

  border: none;
  border-radius: 7px;

  background-color: ${hexToRGBA('#078FF1', 0.1)};
  background-repeat: no-repeat;
  background-origin: content-box;
  background-position-x: right;

  ${({ isValid }) => isValid && css`
    border: 1px solid #28A745;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_success.svg)`};
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
  `};

  ${({ isInvalid }) => isInvalid && css`
    border: 1px solid #DC3545;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_failure.svg)`};
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
  `};

  &:focus {
    background-color: #FFFFFF;
    border-color: #80BDFF;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgb(0 123 255 / 25%);
  }
`;

const StyledFeedback = styled(Form.Control.Feedback)`

`;

function Textarea({
  id,
  hint,
  label,
  emptyLabel,
  placeholder,
  required,
  validated,
  isInvalid,
  pattern,
  maxLength,
  minLength,
  defaultInvalidMessage = '',
  validate,
  onKeyPress,
  matchValue,
  value = '',
  onChange,
}) {
  const handleChange = useCallback((event) => {
    validate && validate(event, { required, matchValue });
    onChange && onChange(event);
  }, [onChange, validate, required, matchValue]);

  const overlayTriggerProps = useMemo(() => {
    const props = {
      placement: isMobile ? "top" : "right-start",
      delay: { show: 0, hide: 0 },
      trigger: ["hover", "focus"],
      overlay: (
        <Tooltip id={`tooltip-${id}`} className="hintTooltip">
          {hint}
        </Tooltip>
      ),
    };

    if (!hint || isMobile) {
      props.show = false;
    }

    return props;
  }, [id, hint]);

  const valid = !required && validated && !isInvalid || value && validated && !isInvalid;
  const invalid = isInvalid || (required && !value && validated);

  return (
    <OverlayTrigger {...overlayTriggerProps}>
      <StyledGroup controlId={id} withLabel={Boolean(label || emptyLabel)}>
        <StyledLabel>
          {label}
          {!emptyLabel && label && required && (<span>&nbsp;*</span>)}
        </StyledLabel>
        <StyledControl
          as="textarea"
          rows={5}
          name={id}
          required={required}
          placeholder={placeholder}
          pattern={pattern}
          maxLength={maxLength}
          minLength={minLength}
          isValid={valid}
          isInvalid={invalid}
          value={value}
          onKeyPress={onKeyPress}
          onChange={handleChange}
          data-hj-allow
        />
        <StyledFeedback type="invalid">
          {defaultInvalidMessage}
        </StyledFeedback>
      </StyledGroup>
    </OverlayTrigger>
  );
}

export default Textarea;
